// src/components/Layout.jsx - Complete with all translations including improved cart message
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, useContext, createContext } from 'react';
import { 
  FaBars, FaTimes, FaChevronDown, FaGlobe, FaUser, FaUserPlus,
  FaFacebook, FaTwitter, FaInstagram, FaWhatsapp, FaWifi, FaEnvelope,
  FaHouseUser, FaServer, FaShoppingCart
} from "react-icons/fa";
import nexcomLogo from '../assets/nexcom-logo.png';

// ─── Language Context ───
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: 'en',
      toggleLanguage: () => {},
      t: (key) => {
        return translations.en[key] || key;
      },
    };
  }
  return context;
};

// ─── Translations ───
const translations = {
  en: {
    // Navigation
    'nav.internet': 'Internet',
    'nav.wifi': 'WiFi',
    'nav.devices': 'Devices',
    'nav.contact': 'Contact',
    'nav.getStarted': 'Get Started',
    'nav.login': 'Login',
    'nav.signup': 'Signup',
    'nav.cart': 'Cart',
    'nav.changeLanguage': 'Change Language',
    'lang.english': 'English',
    'lang.swahili': 'Kiswahili',
    
    // Footer
    'footer.brandText': 'Fixed wireless internet for homes and businesses in Kisumu. Built on over 20 years of network operations across East Africa. CA Licensed NFP Tier 2 ISP.',
    'footer.services': 'Services',
    'footer.support': 'Support',
    'footer.about': 'About Us',
    'footer.legal': 'Legal',
    'footer.faqs': 'FAQs',
    'footer.contactUs': 'Contact Us',
    'footer.requestCallback': 'Request Callback',
    'footer.quickPay': 'Quick Pay',
    'footer.aboutUs': 'About Us',
    'footer.coverageMap': 'Coverage Map',
    'footer.careers': 'Careers',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions',
    'footer.cookies': 'Cookie Policy',
    'footer.dataProtection': 'Data Protection',
    'footer.rights': 'All Rights Reserved.',

    // Support Page
    'support.title': 'Support Center',
    'support.subtitle': "We're here to help you stay connected",
    'support.faq.title': 'Frequently Asked Questions',
    'support.faq.q1': 'How do I check coverage in my area?',
    'support.faq.a1': 'Visit our ',
    'support.faq.coverageLink': 'Coverage Map',
    'support.faq.a1b': ' page or contact us directly to check if your location is covered.',
    'support.faq.q2': 'What equipment do I need?',
    'support.faq.a2': 'We provide all necessary equipment upon approval of your application. Installation is included.',
    'support.faq.q3': 'How long does installation take?',
    'support.faq.a3': 'Installation typically takes 24-48 hours after approval and payment confirmation.',
    'support.faq.q4': 'Do you have data caps?',
    'support.faq.a4': 'No! All our residential and business plans include unlimited data with no caps.',
    'support.faq.q5': 'What payment methods do you accept?',
    'support.faq.a5': 'We accept M-Pesa, bank transfers, and cash payments at our offices.',
    'support.contact.title': 'Contact Information',
    'support.contact.phone': 'Phone',
    'support.contact.chat': 'Chat with us',
    'support.contact.email': 'Email',
    'support.contact.location': 'Location',
    'support.contact.locationValue': 'Kisumu, Kenya',
    'support.contact.hours': 'Business Hours',
    'support.contact.monFri': 'Monday - Friday: 8:00 AM - 6:00 PM',
    'support.contact.sat': 'Saturday: 9:00 AM - 2:00 PM',
    'support.contact.button': 'Contact Support',

    // Contact Page
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in touch with our team',
    'contact.getInTouch': 'Get in Touch',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.location': 'Location',
    'contact.locationValue': 'Kisumu, Kenya',
    'contact.hours': 'Hours',
    'contact.monFri': 'Mon-Fri: 8AM - 6PM',
    'contact.sat': 'Sat: 9AM - 2PM',
    'contact.sendMessage': 'Send a Message',
    'contact.form.name': 'Full Name *',
    'contact.form.email': 'Email Address',
    'contact.form.phone': 'Phone Number *',
    'contact.form.selectService': 'Select Service',
    'contact.form.equipment': 'Equipment',
    'contact.form.message': 'Your Message',
    'contact.form.send': 'Send Message',

    // About Page
    'about.title': 'About Nexcom',
    'about.subtitle': 'Connecting Kenya with reliable internet since 2005',
    'about.story': 'Our Story',
    'about.storyText1': 'Nexcom is a CA Licensed NFP Tier 2 ISP with over 20 years of network operations experience across East Africa. We specialize in providing fixed wireless internet solutions for homes and businesses in Kisumu and surrounding areas.',
    'about.storyText2': 'Our mission is to deliver high-speed, reliable, and affordable internet connectivity that empowers individuals and businesses to thrive in the digital economy.',
    'about.whyChoose': 'Why Choose Nexcom',
    'about.feature1': '20+ Years of Experience',
    'about.feature2': '10,000+ Satisfied Customers',
    'about.feature3': 'Fiber-fast Wireless Technology',
    'about.feature4': 'Licensed & Regulated by CAK',
    'about.feature5': 'East Africa Network Operations',
    'about.stat1': 'Years of Experience',
    'about.stat2': 'Happy Customers',
    'about.stat3': 'Uptime Guarantee',
    'about.stat4': 'Customer Support',
    'about.ctaTitle': 'Ready to get connected?',
    'about.ctaButton': 'Contact Us Today',

    // Careers Page
    'careers.title': 'Careers at Nexcom',
    'careers.subtitle': 'Join our team and help connect Kenya to the future',
    'careers.badge': 'Join our growing team',
    'careers.intro': "At Nexcom, we're passionate about providing reliable internet connectivity to homes and businesses across Kenya. We're looking for talented individuals who share our vision and want to make a difference.",
    'careers.value1': 'Teamwork',
    'careers.value2': 'Innovation',
    'careers.value3': 'Excellence',
    'careers.openings': 'Current Openings',
    'careers.start': 'Immediate Start',
    'careers.apply': 'Apply Now',
    'careers.ctaTitle': "Don't see a role that fits?",
    'careers.ctaText': "We're always looking for talented people. Send us your CV.",
    'careers.ctaButton': 'Send Your CV',
    'careers.job1.title': 'Network Engineer',
    'careers.job1.type': 'Full Time',
    'careers.job1.description': 'Manage and maintain our fixed wireless network infrastructure. Ensure optimal performance and reliability.',
    'careers.job2.title': 'Customer Support Specialist',
    'careers.job2.type': 'Full Time',
    'careers.job2.description': 'Provide excellent customer service and technical support to our growing customer base.',
    'careers.job3.title': 'Sales Representative',
    'careers.job3.type': 'Full Time',
    'careers.job3.description': 'Drive sales and expand our customer base in Kisumu region. Build relationships with potential clients.',
    'careers.job4.title': 'Installation Technician',
    'careers.job4.type': 'Full Time',
    'careers.job4.description': 'Install and set up internet equipment for new customers. Provide on-site technical support.',

    // Devices Page
    'devices.eyebrow': 'Equipment & Accessories',
    'devices.pageTitle': 'Power Your Connection.',
    'devices.pageSubtitle': 'High-performance networking devices designed for speed and reliability. Get connected with Nexcom\'s premium equipment.',
    'devices.sectionTitle': 'Networking Devices',
    'devices.addToCart': 'Add to Cart',
    'devices.ctaTitle': 'Ready to Get Connected?',
    'devices.ctaText': 'Purchase your device today and get started with Nexcom\'s high-speed internet.',
    'devices.ctaShop': 'Shop Now',
    'devices.ctaContact': 'Contact Sales',

    // Devices Categories
    'devices.category.routers': 'Routers & Gateways',
    'devices.category.routersDesc': 'High-performance routers for seamless connectivity throughout your home or office.',
    'devices.category.extenders': 'Extenders & Boosters',
    'devices.category.extendersDesc': 'Extend your WiFi coverage to every corner of your space with these powerful boosters.',
    'devices.category.mobile': 'Mobile & Portable',
    'devices.category.mobileDesc': 'Portable connectivity solutions for internet on the go.',

    // Router Pro
    'devices.routerPro.name': 'Nexcom Router Pro',
    'devices.routerPro.description': 'Dual-band WiFi 6 router with advanced security features',
    'devices.routerPro.feature1': 'Dual-band WiFi',
    'devices.routerPro.feature2': 'Up to 300Mbps',
    'devices.routerPro.feature3': 'Covers 1500 sq ft',
    'devices.routerPro.feature4': 'Supports 32 devices',
    'devices.routerPro.badge': 'Best Seller',

    // Router Lite
    'devices.routerLite.name': 'Nexcom Router Lite',
    'devices.routerLite.description': 'Affordable router for basic home connectivity needs',
    'devices.routerLite.feature1': 'Single-band WiFi',
    'devices.routerLite.feature2': 'Up to 150Mbps',
    'devices.routerLite.feature3': 'Covers 800 sq ft',
    'devices.routerLite.feature4': 'Supports 16 devices',
    'devices.routerLite.badge': 'Budget',

    // Mesh System
    'devices.mesh.name': 'Nexcom Mesh System',
    'devices.mesh.description': 'Whole-home mesh WiFi system with seamless roaming',
    'devices.mesh.feature1': 'Tri-band WiFi',
    'devices.mesh.feature2': 'Up to 600Mbps',
    'devices.mesh.feature3': 'Covers 3000 sq ft',
    'devices.mesh.feature4': 'Supports 64 devices',
    'devices.mesh.badge': 'Premium',

    // Extender Plus
    'devices.extenderPlus.name': 'Nexcom Extender Plus',
    'devices.extenderPlus.description': 'High-gain WiFi extender for large homes and offices',
    'devices.extenderPlus.feature1': 'Plug and play',
    'devices.extenderPlus.feature2': 'Up to 300Mbps',
    'devices.extenderPlus.feature3': 'Covers 1000 sq ft',
    'devices.extenderPlus.feature4': 'Easy setup',
    'devices.extenderPlus.badge': 'Value Pick',

    // Extender Mini
    'devices.extenderMini.name': 'Nexcom Extender Mini',
    'devices.extenderMini.description': 'Compact WiFi booster for smaller spaces',
    'devices.extenderMini.feature1': 'Plug and play',
    'devices.extenderMini.feature2': 'Up to 150Mbps',
    'devices.extenderMini.feature3': 'Covers 500 sq ft',
    'devices.extenderMini.feature4': 'Compact design',
    'devices.extenderMini.badge': 'Compact',

    // Outdoor Extender
    'devices.extenderOutdoor.name': 'Nexcom Outdoor Extender',
    'devices.extenderOutdoor.description': 'Weatherproof WiFi extender for outdoor coverage',
    'devices.extenderOutdoor.feature1': 'Weatherproof',
    'devices.extenderOutdoor.feature2': 'Up to 300Mbps',
    'devices.extenderOutdoor.feature3': 'Covers 2000 sq ft',
    'devices.extenderOutdoor.feature4': 'IP65 rated',
    'devices.extenderOutdoor.badge': 'Outdoor',

    // MiFi 5G
    'devices.mifi5g.name': 'Nexcom MiFi 5G',
    'devices.mifi5g.description': 'Next-gen portable hotspot with 5G connectivity',
    'devices.mifi5g.feature1': '4G LTE/5G',
    'devices.mifi5g.feature2': 'Up to 12 hours battery',
    'devices.mifi5g.feature3': 'Connects 16 devices',
    'devices.mifi5g.feature4': 'Pocket-sized',
    'devices.mifi5g.badge': 'Portable',

    // MiFi 4G
    'devices.mifi4g.name': 'Nexcom MiFi 4G',
    'devices.mifi4g.description': 'Reliable 4G LTE portable hotspot for daily use',
    'devices.mifi4g.feature1': '4G LTE',
    'devices.mifi4g.feature2': 'Up to 8 hours battery',
    'devices.mifi4g.feature3': 'Connects 10 devices',
    'devices.mifi4g.feature4': 'Lightweight',
    'devices.mifi4g.badge': 'Compact',

    // Fixed Hub
    'devices.fixedHub.name': 'Nexcom Fixed Hub',
    'devices.fixedHub.description': 'Enterprise-grade fixed wireless solution for businesses',
    'devices.fixedHub.feature1': '4G LTE/3G',
    'devices.fixedHub.feature2': '32 concurrent users',
    'devices.fixedHub.feature3': '2000mAh battery',
    'devices.fixedHub.feature4': 'VoLTE support',
    'devices.fixedHub.badge': 'Business',

    'devices.cart.title': 'Shopping Cart',
    'devices.cart.empty': 'Your cart is empty',
    'devices.cart.emptySub': 'Start adding devices!',
    'devices.cart.total': 'Total:',
    'devices.cart.clear': 'Clear',
    'devices.cart.checkout': 'Checkout',
    'devices.cart.loginMessage': '🔒 Please log in to complete your purchase. You\'ll be redirected to our secure login page.',

    'devices.checkout.title': 'Complete Payment',
    'devices.checkout.subtitle': 'Enter your M-Pesa number to receive payment request',
    'devices.checkout.phoneLabel': 'M-Pesa Phone Number',
    'devices.checkout.phonePlaceholder': '7080xxxxxx',
    'devices.checkout.phoneHint': 'Enter 10-digit number (e.g., 0708xxxxxx)',
    'devices.checkout.payButton': 'Pay with M-Pesa',
    'devices.checkout.processing': 'Processing...',
    'devices.checkout.secure': 'Secured by Nexcom • STK Push will be sent to your phone',
    'devices.checkout.success': 'Payment Successful!',
    'devices.checkout.successText': 'Your order has been confirmed. You will receive a confirmation shortly.',
    'devices.checkout.error': 'Payment Failed',
    'devices.checkout.errorText': 'Please try again or use a different payment method.',
    'devices.checkout.retry': 'Try Again',
    'devices.checkout.item': 'Item',
    'devices.checkout.qty': 'Qty',
    'devices.checkout.amount': 'Amount',
    'devices.payment.invalidPhone': 'Please enter a valid 10-digit phone number (e.g., 0708xxxxxx)',

    // Legal Page
    'legal.lastUpdated': 'Last updated:',
    'legal.lastUpdatedDate': 'January 2025',

    'legal.privacy.title': 'Privacy Policy',
    'legal.privacy.section1': 'Information We Collect',
    'legal.privacy.section1Text': 'Nexcom collects information to provide better services to all our customers. This includes personal information such as name, email address, phone number, and physical address.',
    'legal.privacy.section2': 'How We Use Information',
    'legal.privacy.section2Text': 'We use the information we collect to:',
    'legal.privacy.list1': 'Provide, maintain, and improve our services',
    'legal.privacy.list2': 'Process your orders and payments',
    'legal.privacy.list3': 'Send you technical notices and support messages',
    'legal.privacy.list4': 'Communicate with you about products and services',
    'legal.privacy.list5': 'Comply with legal obligations',
    'legal.privacy.section3': 'Data Security',
    'legal.privacy.section3Text': 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',

    'legal.terms.title': 'Terms & Conditions',
    'legal.terms.section1': 'Acceptance of Terms',
    'legal.terms.section1Text': 'By using Nexcom services, you agree to these Terms and Conditions. If you do not agree, please do not use our services.',
    'legal.terms.section2': 'Service Description',
    'legal.terms.section2Text': 'Nexcom provides fixed wireless internet services to residential and business customers in Kisumu, Kenya. Services are subject to availability and coverage area.',
    'legal.terms.section3': 'Payment and Billing',
    'legal.terms.list1': 'All payments are due on the 1st of each month',
    'legal.terms.list2': 'Late payments may result in service suspension',
    'legal.terms.list3': 'Installation fees are non-refundable',
    'legal.terms.list4': 'Prices are subject to change with 30 days notice',

    'legal.cookies.title': 'Cookie Policy',
    'legal.cookies.section1': 'What Are Cookies',
    'legal.cookies.section1Text': 'Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience.',
    'legal.cookies.section2': 'How We Use Cookies',
    'legal.cookies.section2Text': 'We use cookies to:',
    'legal.cookies.list1': 'Remember your preferences',
    'legal.cookies.list2': 'Analyze how you use our website',
    'legal.cookies.list3': 'Personalize content and ads',
    'legal.cookies.list4': 'Improve our services',

    'legal.service.title': 'Service Agreement',
    'legal.service.section1': 'Service Level Agreement',
    'legal.service.section1Text': 'Nexcom commits to providing reliable internet services with the following service levels:',
    'legal.service.list1': '99.9% network uptime guarantee',
    'legal.service.list2': '4-hour response time for business customers',
    'legal.service.list3': '24-hour resolution time for residential customers',
    'legal.service.list4': '24/7 technical support availability',

    'legal.dataprotection.title': 'Data Protection Policy',
    'legal.dataprotection.section1': 'Our Commitment to Data Protection',
    'legal.dataprotection.section1Text': 'At Nexcom, we are committed to protecting the privacy and security of your personal data. This Data Protection Policy explains how we collect, use, store, and protect your personal information in compliance with the Kenya Data Protection Act, 2019 and other applicable data protection laws.',
    'legal.dataprotection.section2': 'Data Protection Principles',
    'legal.dataprotection.section2Text': 'We adhere to the following data protection principles:',
    'legal.dataprotection.principle1': 'Lawfulness, Fairness, and Transparency:',
    'legal.dataprotection.principle1Text': 'We process personal data lawfully, fairly, and in a transparent manner.',
    'legal.dataprotection.principle2': 'Purpose Limitation:',
    'legal.dataprotection.principle2Text': 'We collect personal data for specified, explicit, and legitimate purposes.',
    'legal.dataprotection.principle3': 'Data Minimization:',
    'legal.dataprotection.principle3Text': 'We collect only the personal data that is necessary for the intended purpose.',
    'legal.dataprotection.principle4': 'Accuracy:',
    'legal.dataprotection.principle4Text': 'We keep personal data accurate and up to date.',
    'legal.dataprotection.principle5': 'Storage Limitation:',
    'legal.dataprotection.principle5Text': 'We retain personal data only for as long as necessary.',
    'legal.dataprotection.principle6': 'Integrity and Confidentiality:',
    'legal.dataprotection.principle6Text': 'We process personal data securely, protecting against unauthorized or unlawful processing.',
    'legal.dataprotection.principle7': 'Accountability:',
    'legal.dataprotection.principle7Text': 'We take responsibility for complying with data protection principles.',
    'legal.dataprotection.section3': 'Your Data Protection Rights',
    'legal.dataprotection.section3Text': 'Under the Kenya Data Protection Act, 2019, you have the following rights:',
    'legal.dataprotection.right1': 'Right to Access:',
    'legal.dataprotection.right1Text': 'You have the right to request access to your personal data.',
    'legal.dataprotection.right2': 'Right to Rectification:',
    'legal.dataprotection.right2Text': 'You have the right to request correction of inaccurate personal data.',
    'legal.dataprotection.right3': 'Right to Erasure:',
    'legal.dataprotection.right3Text': 'You have the right to request deletion of your personal data under certain circumstances.',
    'legal.dataprotection.right4': 'Right to Restrict Processing:',
    'legal.dataprotection.right4Text': 'You have the right to request restriction of processing of your personal data.',
    'legal.dataprotection.right5': 'Right to Data Portability:',
    'legal.dataprotection.right5Text': 'You have the right to receive your personal data in a structured, commonly used format.',
    'legal.dataprotection.right6': 'Right to Object:',
    'legal.dataprotection.right6Text': 'You have the right to object to processing of your personal data.',
    'legal.dataprotection.right7': 'Right to Lodge a Complaint:',
    'legal.dataprotection.right7Text': 'You have the right to lodge a complaint with the Office of the Data Protection Commissioner (ODPC).',
    'legal.dataprotection.section4': 'How We Collect Your Data',
    'legal.dataprotection.section4Text': 'We collect personal data through various channels including:',
    'legal.dataprotection.collect1': 'Online forms and applications',
    'legal.dataprotection.collect2': 'Telephone calls and SMS communications',
    'legal.dataprotection.collect3': 'Email correspondence',
    'legal.dataprotection.collect4': 'Service installations and on-site visits',
    'legal.dataprotection.collect5': 'Customer support interactions',
    'legal.dataprotection.section5': 'Types of Data We Collect',
    'legal.dataprotection.section5Text': 'We collect the following categories of personal data:',
    'legal.dataprotection.type1': 'Identity Data:',
    'legal.dataprotection.type1Text': 'Name, ID number, date of birth, gender',
    'legal.dataprotection.type2': 'Contact Data:',
    'legal.dataprotection.type2Text': 'Address, email address, phone numbers',
    'legal.dataprotection.type3': 'Financial Data:',
    'legal.dataprotection.type3Text': 'Payment details, billing information',
    'legal.dataprotection.type4': 'Technical Data:',
    'legal.dataprotection.type4Text': 'IP address, device information, usage statistics',
    'legal.dataprotection.type5': 'Service Data:',
    'legal.dataprotection.type5Text': 'Service preferences, account history, support tickets',
    'legal.dataprotection.section6': 'How We Use Your Data',
    'legal.dataprotection.section6Text': 'We use your personal data for the following purposes:',
    'legal.dataprotection.use1': 'To provide and maintain our internet services',
    'legal.dataprotection.use2': 'To process payments and manage billing',
    'legal.dataprotection.use3': 'To provide customer support and respond to inquiries',
    'legal.dataprotection.use4': 'To send service updates and technical notices',
    'legal.dataprotection.use5': 'To comply with legal and regulatory requirements',
    'legal.dataprotection.use6': 'To improve our services and customer experience',
    'legal.dataprotection.use7': 'To prevent fraud and ensure network security',
    'legal.dataprotection.section7': 'Data Security Measures',
    'legal.dataprotection.section7Text': 'We implement robust security measures to protect your personal data:',
    'legal.dataprotection.security1': 'Encryption of data in transit and at rest',
    'legal.dataprotection.security2': 'Access controls and authentication mechanisms',
    'legal.dataprotection.security3': 'Regular security audits and vulnerability assessments',
    'legal.dataprotection.security4': 'Employee training on data protection and security',
    'legal.dataprotection.security5': 'Incident response procedures for data breaches',
    'legal.dataprotection.section8': 'Data Sharing and Disclosure',
    'legal.dataprotection.section8Text': 'We may share your personal data with:',
    'legal.dataprotection.share1': 'Service Providers:',
    'legal.dataprotection.share1Text': 'Third-party vendors who assist in providing our services',
    'legal.dataprotection.share2': 'Regulatory Authorities:',
    'legal.dataprotection.share2Text': 'When required by law or to comply with legal obligations',
    'legal.dataprotection.share3': 'Law Enforcement:',
    'legal.dataprotection.share3Text': 'When necessary for crime prevention or investigation',
    'legal.dataprotection.shareNote': 'We do not sell or rent your personal data to third parties for marketing purposes.',
    'legal.dataprotection.section9': 'Data Retention Policy',
    'legal.dataprotection.section9Text': 'We retain your personal data for the duration of your relationship with us and for a reasonable period thereafter to comply with legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we securely delete or anonymize it.',
    'legal.dataprotection.section10': "Children's Privacy",
    'legal.dataprotection.section10Text': 'Our services are not directed at children under the age of 18. We do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child, we will take steps to delete it.',
    'legal.dataprotection.section11': 'Cross-Border Data Transfers',
    'legal.dataprotection.section11Text': 'Some of our service providers may be located outside Kenya. When we transfer personal data across borders, we ensure that appropriate safeguards are in place to protect your data in accordance with the Kenya Data Protection Act, 2019.',
    'legal.dataprotection.section12': 'Contact Our Data Protection Officer',
    'legal.dataprotection.section12Text': 'If you have any questions about this Data Protection Policy or wish to exercise your data protection rights, please contact our Data Protection Officer:',
    'legal.dataprotection.contactEmail': 'Email:',
    'legal.dataprotection.contactPhone': 'Phone:',
    'legal.dataprotection.contactAddress': 'Address:',
    'legal.dataprotection.section13': 'Reporting a Data Breach',
    'legal.dataprotection.section13Text': 'If you suspect a data breach or have concerns about your personal data, please report it immediately to our Data Protection Officer at dpo@nexcom.ke. We will investigate and take appropriate action, including notifying the Office of the Data Protection Commissioner (ODPC) if required.',
  },
  sw: {
    // Navigation
    'nav.internet': 'Intaneti',
    'nav.wifi': 'WiFi',
    'nav.devices': 'Vifaa',
    'nav.contact': 'Wasiliana',
    'nav.getStarted': 'Anza',
    'nav.login': 'Ingia',
    'nav.signup': 'Jisajili',
    'nav.cart': 'Kikapu',
    'nav.changeLanguage': 'Badilisha Lugha',
    'lang.english': 'Kiingereza',
    'lang.swahili': 'Kiswahili',
    
    // Footer
    'footer.brandText': 'Intaneti ya waya isiyo na waya kwa nyumba na biashara Kisumu. Imejengwa juu ya zaidi ya miaka 20 ya uendeshaji wa mtandao Afrika Mashariki. CA Licensed NFP Tier 2 ISP.',
    'footer.services': 'Huduma',
    'footer.support': 'Msaada',
    'footer.about': 'Kuhusu Sisi',
    'footer.legal': 'Sheria',
    'footer.faqs': 'Maswali',
    'footer.contactUs': 'Wasiliana Nasi',
    'footer.requestCallback': 'Omba Mrejesho',
    'footer.quickPay': 'Lipa Haraka',
    'footer.aboutUs': 'Kuhusu Sisi',
    'footer.coverageMap': 'Ramani ya Ufikiaji',
    'footer.careers': 'Kazi',
    'footer.privacy': 'Sera ya Faragha',
    'footer.terms': 'Masharti na Mashart',
    'footer.cookies': 'Sera ya Kuki',
    'footer.dataProtection': 'Ulinzi wa Data',
    'footer.rights': 'Haki Zote Zimehifadhiwa.',

    // Support Page
    'support.title': 'Kituo cha Msaada',
    'support.subtitle': 'Tuko hapa kukusaidia kubaki na muunganisho',
    'support.faq.title': 'Maswali Yanayoulizwa Mara kwa Mara',
    'support.faq.q1': 'Je, ninawezaje kuangalia eneo langu?',
    'support.faq.a1': 'Tembelea ',
    'support.faq.coverageLink': 'Ramani ya Ufikiaji',
    'support.faq.a1b': ' au wasiliana nasi moja kwa moja kuangalia kama eneo lako limefunikwa.',
    'support.faq.q2': 'Je, ninahitaji vifaa gani?',
    'support.faq.a2': 'Tunatoa vifaa vyote muhimu baada ya idhini ya ombi lako. Ufungaji umejumuishwa.',
    'support.faq.q3': 'Inachukua muda gani kufunga?',
    'support.faq.a3': 'Ufungaji kwa kawaida huchukua saa 24-48 baada ya idhini na uthibitisho wa malipo.',
    'support.faq.q4': 'Je, mna vikomo vya data?',
    'support.faq.a4': 'Hapana! Mipango yetu yote ya makazi na biashara inajumuisha data isiyo na kikomo bila vikomo.',
    'support.faq.q5': 'Je, mnakubali njia gani za malipo?',
    'support.faq.a5': 'Tunakubali M-Pesa, uhamisho wa benki, na malipo ya pesa taslimu katika ofisi zetu.',
    'support.contact.title': 'Mawasiliano',
    'support.contact.phone': 'Simu',
    'support.contact.chat': 'Wasiliana nasi',
    'support.contact.email': 'Barua Pepe',
    'support.contact.location': 'Eneo',
    'support.contact.locationValue': 'Kisumu, Kenya',
    'support.contact.hours': 'Saa za Kufungua',
    'support.contact.monFri': 'Jumatatu - Ijumaa: 8:00 AM - 6:00 PM',
    'support.contact.sat': 'Jumamosi: 9:00 AM - 2:00 PM',
    'support.contact.button': 'Wasiliana na Msaada',

    // Contact Page
    'contact.title': 'Wasiliana Nasi',
    'contact.subtitle': 'Wasiliana na timu yetu',
    'contact.getInTouch': 'Wasiliana',
    'contact.phone': 'Simu',
    'contact.email': 'Barua Pepe',
    'contact.location': 'Eneo',
    'contact.locationValue': 'Kisumu, Kenya',
    'contact.hours': 'Saa za Kufungua',
    'contact.monFri': 'Jumatatu-Ijumaa: 8AM - 6PM',
    'contact.sat': 'Jumamosi: 9AM - 2PM',
    'contact.sendMessage': 'Tuma Ujumbe',
    'contact.form.name': 'Jina Kamili *',
    'contact.form.email': 'Barua Pepe',
    'contact.form.phone': 'Nambari ya Simu *',
    'contact.form.selectService': 'Chagua Huduma',
    'contact.form.equipment': 'Vifaa',
    'contact.form.message': 'Ujumbe Wako',
    'contact.form.send': 'Tuma Ujumbe',

    // About Page
    'about.title': 'Kuhusu Nexcom',
    'about.subtitle': 'Kuunganisha Kenya na intaneti ya kuaminika tangu 2005',
    'about.story': 'Hadithi Yetu',
    'about.storyText1': 'Nexcom ni ISP ya NFP Tier 2 iliyoidhinishwa na CA yenye zaidi ya miaka 20 ya uzoefu wa uendeshaji wa mtandao katika Afrika Mashariki. Tunataalamu katika kutoa suluhisho za intaneti ya waya isiyo na waya kwa nyumba na biashara Kisumu na maeneo jirani.',
    'about.storyText2': 'Dhamira yetu ni kutoa muunganisho wa intaneti wa kasi, wa kuaminika, na wa bei nafuu unaowawezesha watu binafsi na biashara kustawi katika uchumi wa kidijitali.',
    'about.whyChoose': 'Kwa Nini Chagua Nexcom',
    'about.feature1': 'Miaka 20+ ya Uzoefu',
    'about.feature2': 'Wateja 10,000+ Walioridhika',
    'about.feature3': 'Teknolojia ya Wireless ya Kasi ya Fiber',
    'about.feature4': 'Imeidhinishwa na Kudhibitiwa na CAK',
    'about.feature5': 'Uendeshaji wa Mtandao Afrika Mashariki',
    'about.stat1': 'Miaka ya Uzoefu',
    'about.stat2': 'Wateja Walioridhika',
    'about.stat3': 'Uhakikisho wa Upatikanaji',
    'about.stat4': 'Msaada kwa Wateja',
    'about.ctaTitle': 'Uko tayari kuunganishwa?',
    'about.ctaButton': 'Wasiliana Nasi Leo',

    // Careers Page
    'careers.title': 'Kazi katika Nexcom',
    'careers.subtitle': 'Jiunge na timu yetu na kusaidia kuunganisha Kenya kwa maisha ya baadaye',
    'careers.badge': 'Jiunge na timu yetu inayokua',
    'careers.intro': 'Katika Nexcom, tuna shauku ya kutoa muunganisho wa intaneti wa kuaminika kwa nyumba na biashara kote Kenya. Tunatafuta watu wenye vipaji ambao wanashiriki maono yetu na wanataka kuleta mabadiliko.',
    'careers.value1': 'Ushirikiano',
    'careers.value2': 'Ubunifu',
    'careers.value3': 'Ubora',
    'careers.openings': 'Nafasi Zilizopo',
    'careers.start': 'Anza Mara Moja',
    'careers.apply': 'Omba Sasa',
    'careers.ctaTitle': 'Huoni nafasi inayokufaa?',
    'careers.ctaText': 'Daima tunatafuta watu wenye vipaji. Tuma CV yako kwetu.',
    'careers.ctaButton': 'Tuma CV Yako',
    'careers.job1.title': 'Mhandisi wa Mtandao',
    'careers.job1.type': 'Muda Wote',
    'careers.job1.description': 'Simamia na udumishe miundombinu yetu ya mtandao wa waya isiyo na waya. Hakikisha utendaji bora na kuegemea.',
    'careers.job2.title': 'Mtaalamu wa Msaada kwa Wateja',
    'careers.job2.type': 'Muda Wote',
    'careers.job2.description': 'Toa huduma bora kwa wateja na msaada wa kiufundi kwa wateja wetu wanaokua.',
    'careers.job3.title': 'Mwakilishi wa Mauzo',
    'careers.job3.type': 'Muda Wote',
    'careers.job3.description': 'Endeleza mauzo na panua wateja wetu katika mkoa wa Kisumu. Jenga uhusiano na wateja wanaowezekana.',
    'careers.job4.title': 'Mtaalamu wa Ufungaji',
    'careers.job4.type': 'Muda Wote',
    'careers.job4.description': 'Sakinisha na usanidi vifaa vya intaneti kwa wateja wapya. Toa msaada wa kiufundi mahali pa kazi.',

    // Devices Page - Swahili
    'devices.eyebrow': 'Vifaa na Vifaa',
    'devices.pageTitle': 'Nguvu ya Muunganisho Wako.',
    'devices.pageSubtitle': 'Vifaa vya mtandao vya utendaji wa juu vilivyoundwa kwa kasi na kuegemea. Unganisha na vifaa vya ubora wa Nexcom.',
    'devices.sectionTitle': 'Vifaa vya Mtandao',
    'devices.addToCart': 'Ongeza Kwenye Kikapu',
    'devices.ctaTitle': 'Uko Tayari Kuunganishwa?',
    'devices.ctaText': 'Nunua kifaa chako leo na uanze na intaneti ya kasi ya Nexcom.',
    'devices.ctaShop': 'Nunua Sasa',
    'devices.ctaContact': 'Wasiliana na Mauzo',

    // Devices Categories - Swahili
    'devices.category.routers': 'Vipanga njia na Milango',
    'devices.category.routersDesc': 'Vipanga njia vya utendaji wa juu kwa muunganisho usio na usumbufu nyumbani au ofisini kwako.',
    'devices.category.extenders': 'Vipanuzi na Viboreshaji',
    'devices.category.extendersDesc': 'Panua ufikiaji wako wa WiFi kwenye kila kona ya nafasi yako kwa viongeza nguvu hivi.',
    'devices.category.mobile': 'Simu na Kubebeka',
    'devices.category.mobileDesc': 'Suluhisho za muunganisho wa kubebeka kwa intaneti ukiwa njiani.',

    // Router Pro - Swahili
    'devices.routerPro.name': 'Nexcom Router Pro',
    'devices.routerPro.description': 'Router ya WiFi 6 yenye bendi mbili na vipengele vya usalama vya hali ya juu',
    'devices.routerPro.feature1': 'WiFi ya bendi mbili',
    'devices.routerPro.feature2': 'Kasi hadi 300Mbps',
    'devices.routerPro.feature3': 'Inafikia futi 1500 za mraba',
    'devices.routerPro.feature4': 'Inaweza kuunganisha vifaa 32',
    'devices.routerPro.badge': 'Inauzwa Sana',

    // Router Lite - Swahili
    'devices.routerLite.name': 'Nexcom Router Lite',
    'devices.routerLite.description': 'Router ya bei nafuu kwa mahitaji ya msingi ya muunganisho wa nyumbani',
    'devices.routerLite.feature1': 'WiFi ya bendi moja',
    'devices.routerLite.feature2': 'Kasi hadi 150Mbps',
    'devices.routerLite.feature3': 'Inafikia futi 800 za mraba',
    'devices.routerLite.feature4': 'Inaweza kuunganisha vifaa 16',
    'devices.routerLite.badge': 'Bajeti',

    // Mesh System - Swahili
    'devices.mesh.name': 'Mfumo wa Mesh wa Nexcom',
    'devices.mesh.description': 'Mfumo wa mesh WiFi wa nyumba nzima na uhamaji usio na usumbufu',
    'devices.mesh.feature1': 'WiFi ya bendi tatu',
    'devices.mesh.feature2': 'Kasi hadi 600Mbps',
    'devices.mesh.feature3': 'Inafikia futi 3000 za mraba',
    'devices.mesh.feature4': 'Inaweza kuunganisha vifaa 64',
    'devices.mesh.badge': 'Ubora',

    // Extender Plus - Swahili
    'devices.extenderPlus.name': 'Kipanuzi cha Nexcom Plus',
    'devices.extenderPlus.description': 'Kipanuzi cha WiFi chenye nguvu kubwa kwa nyumba na ofisi kubwa',
    'devices.extenderPlus.feature1': 'Chomeka na utumie',
    'devices.extenderPlus.feature2': 'Kasi hadi 300Mbps',
    'devices.extenderPlus.feature3': 'Inafikia futi 1000 za mraba',
    'devices.extenderPlus.feature4': 'Usanidi rahisi',
    'devices.extenderPlus.badge': 'Thamani Bora',

    // Extender Mini - Swahili
    'devices.extenderMini.name': 'Kipanuzi cha Nexcom Mini',
    'devices.extenderMini.description': 'Kiboresha WiFi kompakt kwa nafasi ndogo',
    'devices.extenderMini.feature1': 'Chomeka na utumie',
    'devices.extenderMini.feature2': 'Kasi hadi 150Mbps',
    'devices.extenderMini.feature3': 'Inafikia futi 500 za mraba',
    'devices.extenderMini.feature4': 'Muundo kompakt',
    'devices.extenderMini.badge': 'Kompakt',

    // Outdoor Extender - Swahili
    'devices.extenderOutdoor.name': 'Kipanuzi cha Nje cha Nexcom',
    'devices.extenderOutdoor.description': 'Kipanuzi cha WiFi kisichopenywa na mvua kwa ufikiaji wa nje',
    'devices.extenderOutdoor.feature1': 'Kisichopenywa na mvua',
    'devices.extenderOutdoor.feature2': 'Kasi hadi 300Mbps',
    'devices.extenderOutdoor.feature3': 'Inafikia futi 2000 za mraba',
    'devices.extenderOutdoor.feature4': 'Kiwango cha IP65',
    'devices.extenderOutdoor.badge': 'Nje',

    // MiFi 5G - Swahili
    'devices.mifi5g.name': 'Nexcom MiFi 5G',
    'devices.mifi5g.description': 'Kituo cha uhamaji cha kizazi kipya chenye muunganisho wa 5G',
    'devices.mifi5g.feature1': '4G LTE/5G',
    'devices.mifi5g.feature2': 'Bateri hadi saa 12',
    'devices.mifi5g.feature3': 'Inaunganisha vifaa 16',
    'devices.mifi5g.feature4': 'Cha mfukoni',
    'devices.mifi5g.badge': 'Kubebeka',

    // MiFi 4G - Swahili
    'devices.mifi4g.name': 'Nexcom MiFi 4G',
    'devices.mifi4g.description': 'Kituo cha uhamaji cha 4G LTE cha kuaminika kwa matumizi ya kila siku',
    'devices.mifi4g.feature1': '4G LTE',
    'devices.mifi4g.feature2': 'Bateri hadi saa 8',
    'devices.mifi4g.feature3': 'Inaunganisha vifaa 10',
    'devices.mifi4g.feature4': 'Nyepesi',
    'devices.mifi4g.badge': 'Kompakt',

    // Fixed Hub - Swahili
    'devices.fixedHub.name': 'Kituo cha Wireless Kisichobadilika cha Nexcom',
    'devices.fixedHub.description': 'Suluhisho la wireless la kiwango cha biashara kwa biashara',
    'devices.fixedHub.feature1': '4G LTE/3G',
    'devices.fixedHub.feature2': 'Watumiaji 32 kwa wakati mmoja',
    'devices.fixedHub.feature3': 'Bateri ya 2000mAh',
    'devices.fixedHub.feature4': 'Msaada wa VoLTE',
    'devices.fixedHub.badge': 'Biashara',

    'devices.cart.title': 'Kikapu cha Ununuzi',
    'devices.cart.empty': 'Kikapu chako ni tupu',
    'devices.cart.emptySub': 'Anza kuongeza vifaa!',
    'devices.cart.total': 'Jumla:',
    'devices.cart.clear': 'Futa',
    'devices.cart.checkout': 'Malipo',
    'devices.cart.loginMessage': '🔒 Tafadhali ingia ili kukamilisha ununuzi wako. Utaelekezwa kwenye ukurasa wetu salama wa kuingia.',

    'devices.checkout.title': 'Kamilisha Malipo',
    'devices.checkout.subtitle': 'Weka nambari yako ya M-Pesa kupokea ombi la malipo',
    'devices.checkout.phoneLabel': 'Nambari ya Simu ya M-Pesa',
    'devices.checkout.phonePlaceholder': '7080xxxxxx',
    'devices.checkout.phoneHint': 'Weka nambari yenye tarakimu 10 (mfano, 0708xxxxxx)',
    'devices.checkout.payButton': 'Lipa kwa M-Pesa',
    'devices.checkout.processing': 'Inachakata...',
    'devices.checkout.secure': 'Imehakikishiwa na Nexcom • STK Push itatumwa kwa simu yako',
    'devices.checkout.success': 'Malipo Yamekamilika!',
    'devices.checkout.successText': 'Agizo lako limehakikishiwa. Utapokea uthibitisho hivi karibuni.',
    'devices.checkout.error': 'Malipo Yameshindikana',
    'devices.checkout.errorText': 'Tafadhali jaribu tena au tumia njia nyingine ya malipo.',
    'devices.checkout.retry': 'Jaribu Tena',
    'devices.checkout.item': 'Bidhaa',
    'devices.checkout.qty': 'Idadi',
    'devices.checkout.amount': 'Kiasi',
    'devices.payment.invalidPhone': 'Tafadhali weka nambari ya simu yenye tarakimu 10 (mfano, 0708xxxxxx)',

    // Legal Page - Swahili
    'legal.lastUpdated': 'Iliyosasishwa mwisho:',
    'legal.lastUpdatedDate': 'Januari 2025',

    'legal.privacy.title': 'Sera ya Faragha',
    'legal.privacy.section1': 'Taarifa Tunazokusanya',
    'legal.privacy.section1Text': 'Nexcom hukusanya taarifa ili kutoa huduma bora kwa wateja wetu wote. Hii inajumuisha taarifa za kibinafsi kama vile jina, barua pepe, nambari ya simu, na anwani ya makazi.',
    'legal.privacy.section2': 'Jinsi Tunavyotumia Taarifa',
    'legal.privacy.section2Text': 'Tunatumia taarifa tunazokusanya ili:',
    'legal.privacy.list1': 'Kutoa, kudumisha, na kuboresha huduma zetu',
    'legal.privacy.list2': 'Kuchakata maagizo na malipo yako',
    'legal.privacy.list3': 'Kukutumia notisi za kiufundi na ujumbe wa msaada',
    'legal.privacy.list4': 'Kuwasiliana nawe kuhusu bidhaa na huduma',
    'legal.privacy.list5': 'Kuzingatia majukumu ya kisheria',
    'legal.privacy.section3': 'Usalama wa Data',
    'legal.privacy.section3Text': 'Tunatekeleza hatua zinazofaa za kiufundi na shirika ili kulinda taarifa zako za kibinafsi dhidi ya ufikiaji usioidhinishwa, mabadiliko, ufichuzi, au uharibifu.',

    'legal.terms.title': 'Masharti na Mashart',
    'legal.terms.section1': 'Kukubali Masharti',
    'legal.terms.section1Text': 'Kwa kutumia huduma za Nexcom, unakubali Masharti na Mashart haya. Ikiwa hukubali, tafadhali usitumie huduma zetu.',
    'legal.terms.section2': 'Maelezo ya Huduma',
    'legal.terms.section2Text': 'Nexcom hutoa huduma za intaneti ya waya isiyo na waya kwa wateja wa makazi na biashara Kisumu, Kenya. Huduma zinategemea upatikanaji na eneo la ufikiaji.',
    'legal.terms.section3': 'Malipo na Bili',
    'legal.terms.list1': 'Malipo yote yanatarajiwa tarehe 1 ya kila mwezi',
    'legal.terms.list2': 'Malipo ya kuchelewa yanaweza kusababisha kusimamishwa kwa huduma',
    'legal.terms.list3': 'Ada za usakinishaji hazirejeshwi',
    'legal.terms.list4': 'Bei zinaweza kubadilika kwa notisi ya siku 30',

    'legal.cookies.title': 'Sera ya Kuki',
    'legal.cookies.section1': 'Kuki ni Nini',
    'legal.cookies.section1Text': 'Kuki ni faili ndogo za maandishi zilizohifadhiwa kwenye kifaa chako unapotembelea tovuti yetu. Zinatusaidia kukupa uzoefu bora.',
    'legal.cookies.section2': 'Jinsi Tunavyotumia Kuki',
    'legal.cookies.section2Text': 'Tunatumia kuki ili:',
    'legal.cookies.list1': 'Kukumbuka mapendeleo yako',
    'legal.cookies.list2': 'Kuchambua jinsi unavyotumia tovuti yetu',
    'legal.cookies.list3': 'Kubinafsisha maudhui na matangazo',
    'legal.cookies.list4': 'Kuboresha huduma zetu',

    'legal.service.title': 'Makubaliano ya Huduma',
    'legal.service.section1': 'Makubaliano ya Kiwango cha Huduma',
    'legal.service.section1Text': 'Nexcom inajitolea kutoa huduma za intaneti za kuaminika na viwango vifuatavyo vya huduma:',
    'legal.service.list1': 'Uhakikisho wa 99.9% wa upatikanaji wa mtandao',
    'legal.service.list2': 'Muda wa kujibu wa saa 4 kwa wateja wa biashara',
    'legal.service.list3': 'Muda wa kutatua wa saa 24 kwa wateja wa makazi',
    'legal.service.list4': 'Upatikanaji wa msaada wa kiufundi 24/7',

    'legal.dataprotection.title': 'Sera ya Ulinzi wa Data',
    'legal.dataprotection.section1': 'Ahadi Yetu ya Ulinzi wa Data',
    'legal.dataprotection.section1Text': 'Katika Nexcom, tumejitolea kulinda faragha na usalama wa data yako ya kibinafsi. Sera hii ya Ulinzi wa Data inaelezea jinsi tunavyokusanya, kutumia, kuhifadhi, na kulinda taarifa zako za kibinafsi kwa kufuata Sheria ya Ulinzi wa Data ya Kenya, 2019 na sheria zingine zinazotumika za ulinzi wa data.',
    'legal.dataprotection.section2': 'Kanuni za Ulinzi wa Data',
    'legal.dataprotection.section2Text': 'Tunafuata kanuni zifuatazo za ulinzi wa data:',
    'legal.dataprotection.principle1': 'Uhalali, Uadilifu, na Uwazi:',
    'legal.dataprotection.principle1Text': 'Tunachakata data ya kibinafsi kwa njia halali, ya haki, na kwa uwazi.',
    'legal.dataprotection.principle2': 'Kikomo cha Madhumuni:',
    'legal.dataprotection.principle2Text': 'Tunakusanya data ya kibinafsi kwa madhumuni maalum, wazi, na halali.',
    'legal.dataprotection.principle3': 'Upunguzaji wa Data:',
    'legal.dataprotection.principle3Text': 'Tunakusanya tu data ya kibinafsi ambayo ni muhimu kwa madhumuni yaliyokusudiwa.',
    'legal.dataprotection.principle4': 'Usahihi:',
    'legal.dataprotection.principle4Text': 'Tunaweka data ya kibinafsi sahihi na ya sasa.',
    'legal.dataprotection.principle5': 'Kikomo cha Uhifadhi:',
    'legal.dataprotection.principle5Text': 'Tunahifadhi data ya kibinafsi kwa muda tu inapohitajika.',
    'legal.dataprotection.principle6': 'Uadilifu na Usiri:',
    'legal.dataprotection.principle6Text': 'Tunachakata data ya kibinafsi kwa usalama, kulinda dhidi ya uchakataji usioidhinishwa au usio halali.',
    'legal.dataprotection.principle7': 'Uwajibikaji:',
    'legal.dataprotection.principle7Text': 'Tunachukua jukumu la kufuata kanuni za ulinzi wa data.',
    'legal.dataprotection.section3': 'Haki Zako za Ulinzi wa Data',
    'legal.dataprotection.section3Text': 'Chini ya Sheria ya Ulinzi wa Data ya Kenya, 2019, una haki zifuatazo:',
    'legal.dataprotection.right1': 'Haki ya Kufikia:',
    'legal.dataprotection.right1Text': 'Una haki ya kuomba kufikia data yako ya kibinafsi.',
    'legal.dataprotection.right2': 'Haki ya Marekebisho:',
    'legal.dataprotection.right2Text': 'Una haki ya kuomba marekebisho ya data ya kibinafsi isiyo sahihi.',
    'legal.dataprotection.right3': 'Haki ya Kufuta:',
    'legal.dataprotection.right3Text': 'Una haki ya kuomba kufutwa kwa data yako ya kibinafsi chini ya hali fulani.',
    'legal.dataprotection.right4': 'Haki ya Kuzuia Uchakataji:',
    'legal.dataprotection.right4Text': 'Una haki ya kuomba kuzuia uchakataji wa data yako ya kibinafsi.',
    'legal.dataprotection.right5': 'Haki ya Uhamisho wa Data:',
    'legal.dataprotection.right5Text': 'Una haki ya kupokea data yako ya kibinafsi katika muundo uliopangwa, unaotumika kwa kawaida.',
    'legal.dataprotection.right6': 'Haki ya Kupinga:',
    'legal.dataprotection.right6Text': 'Una haki ya kupinga uchakataji wa data yako ya kibinafsi.',
    'legal.dataprotection.right7': 'Haki ya Kuwasilisha Malalamiko:',
    'legal.dataprotection.right7Text': 'Una haki ya kuwasilisha malalamiko kwa Ofisi ya Kamishna wa Ulinzi wa Data (ODPC).',
    'legal.dataprotection.section4': 'Jinsi Tunavyokusanya Data Yako',
    'legal.dataprotection.section4Text': 'Tunakusanya data ya kibinafsi kupitia njia mbalimbali ikiwa ni pamoja na:',
    'legal.dataprotection.collect1': 'Fomu na maombi ya mtandaoni',
    'legal.dataprotection.collect2': 'Simu na mawasiliano ya SMS',
    'legal.dataprotection.collect3': 'Mawasiliano ya barua pepe',
    'legal.dataprotection.collect4': 'Usakinishaji wa huduma na ziara za mahali',
    'legal.dataprotection.collect5': 'Mwingiliano wa msaada kwa wateja',
    'legal.dataprotection.section5': 'Aina za Data Tunazokusanya',
    'legal.dataprotection.section5Text': 'Tunakusanya aina zifuatazo za data ya kibinafsi:',
    'legal.dataprotection.type1': 'Data ya Utambulisho:',
    'legal.dataprotection.type1Text': 'Jina, nambari ya kitambulisho, tarehe ya kuzaliwa, jinsia',
    'legal.dataprotection.type2': 'Data ya Mawasiliano:',
    'legal.dataprotection.type2Text': 'Anwani, barua pepe, nambari za simu',
    'legal.dataprotection.type3': 'Data ya Kifedha:',
    'legal.dataprotection.type3Text': 'Maelezo ya malipo, taarifa za bili',
    'legal.dataprotection.type4': 'Data ya Kiufundi:',
    'legal.dataprotection.type4Text': 'Anwani ya IP, taarifa za kifaa, takwimu za matumizi',
    'legal.dataprotection.type5': 'Data ya Huduma:',
    'legal.dataprotection.type5Text': 'Mapendeleo ya huduma, historia ya akaunti, tiketi za msaada',
    'legal.dataprotection.section6': 'Jinsi Tunavyotumia Data Yako',
    'legal.dataprotection.section6Text': 'Tunatumia data yako ya kibinafsi kwa madhumuni yafuatayo:',
    'legal.dataprotection.use1': 'Kutoa na kudumisha huduma zetu za intaneti',
    'legal.dataprotection.use2': 'Kuchakata malipo na kusimamia bili',
    'legal.dataprotection.use3': 'Kutoa msaada kwa wateja na kujibu maswali',
    'legal.dataprotection.use4': 'Kutuma sasisho za huduma na notisi za kiufundi',
    'legal.dataprotection.use5': 'Kuzingatia mahitaji ya kisheria na udhibiti',
    'legal.dataprotection.use6': 'Kuboresha huduma zetu na uzoefu wa wateja',
    'legal.dataprotection.use7': 'Kuzuia ulaghai na kuhakikisha usalama wa mtandao',
    'legal.dataprotection.section7': 'Hatua za Usalama wa Data',
    'legal.dataprotection.section7Text': 'Tunatekeleza hatua thabiti za usalama kulinda data yako ya kibinafsi:',
    'legal.dataprotection.security1': 'Usimbaji fiche wa data wakati wa usafirishaji na wakati wa kuhifadhiwa',
    'legal.dataprotection.security2': 'Vidhibiti vya ufikiaji na mifumo ya uthibitishaji',
    'legal.dataprotection.security3': 'Ukaguzi wa mara kwa mara wa usalama na tathmini ya udhaifu',
    'legal.dataprotection.security4': 'Mafunzo ya wafanyakazi kuhusu ulinzi wa data na usalama',
    'legal.dataprotection.security5': 'Taratibu za kukabiliana na matukio ya uvunjaji wa data',
    'legal.dataprotection.section8': 'Kushiriki na Kufichua Data',
    'legal.dataprotection.section8Text': 'Tunaweza kushiriki data yako ya kibinafsi na:',
    'legal.dataprotection.share1': 'Watoa Huduma:',
    'legal.dataprotection.share1Text': 'Wauzaji wengine ambao wanasaidia katika kutoa huduma zetu',
    'legal.dataprotection.share2': 'Mamlaka za Udhibiti:',
    'legal.dataprotection.share2Text': 'Inapotakiwa na sheria au kufuata majukumu ya kisheria',
    'legal.dataprotection.share3': 'Utekelezaji wa Sheria:',
    'legal.dataprotection.share3Text': 'Inapohitajika kwa kuzuia au uchunguzi wa uhalifu',
    'legal.dataprotection.shareNote': 'Hatuuzi au kukodisha data yako ya kibinafsi kwa wahusika wengine kwa madhumuni ya uuzaji.',
    'legal.dataprotection.section9': 'Sera ya Uhifadhi wa Data',
    'legal.dataprotection.section9Text': 'Tunahifadhi data yako ya kibinafsi kwa muda wa uhusiano wako nasi na kwa muda unaofaa baadaye ili kufuata majukumu ya kisheria, kusuluhisha mizozo, na kutekeleza makubaliano yetu. Data inapohitajika tena, tunaifuta kwa usalama au kuifanya isitambulike.',
    'legal.dataprotection.section10': 'Faragha ya Watoto',
    'legal.dataprotection.section10Text': 'Huduma zetu hazielekezwi kwa watoto chini ya umri wa miaka 18. Hatukusanyi kwa makusudi data ya kibinafsi kutoka kwa watoto. Ikiwa tutagundua kwamba tumekusanya data ya kibinafsi kutoka kwa mtoto, tutachukua hatua za kuifuta.',
    'legal.dataprotection.section11': 'Uhamisho wa Data Kuvuka Mipaka',
    'legal.dataprotection.section11Text': 'Baadhi ya watoa huduma wetu wanaweza kuwa nje ya Kenya. Tunapohamisha data ya kibinafsi kuvuka mipaka, tunahakikisha kwamba ulinzi unaofaa umewekwa ili kulinda data yako kwa mujibu wa Sheria ya Ulinzi wa Data ya Kenya, 2019.',
    'legal.dataprotection.section12': 'Wasiliana na Afisa Wetu wa Ulinzi wa Data',
    'legal.dataprotection.section12Text': 'Ikiwa una maswali yoyote kuhusu Sera hii ya Ulinzi wa Data au unataka kutumia haki zako za ulinzi wa data, tafadhali wasiliana na Afisa wetu wa Ulinzi wa Data:',
    'legal.dataprotection.contactEmail': 'Barua Pepe:',
    'legal.dataprotection.contactPhone': 'Simu:',
    'legal.dataprotection.contactAddress': 'Anwani:',
    'legal.dataprotection.section13': 'Kuripoti Uvunjaji wa Data',
    'legal.dataprotection.section13Text': 'Ikiwa unashuku uvunjaji wa data au una wasiwasi kuhusu data yako ya kibinafsi, tafadhali ripoti mara moja kwa Afisa wetu wa Ulinzi wa Data kupitia dpo@nexcom.ke. Tutachunguza na kuchukua hatua zinazofaa, ikiwa ni pamoja na kujulisha Ofisi ya Kamishna wa Ulinzi wa Data (ODPC) ikiwa inahitajika.',
  }
};

// ─── Language Provider ───
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('nexcom-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('nexcom-language', language);
  }, [language]);

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default function Layout({ children }) {
  const { t, language, toggleLanguage } = useLanguage();
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const langDropdownRef = useRef(null);
  const [cartCount, setCartCount] = useState(0);
  const currentYear = new Date().getFullYear();

  // Language options
  const languageOptions = [
    { code: 'en', label: 'English' },
    { code: 'sw', label: 'Kiswahili' },
  ];

  // Listen for cart updates from localStorage
  useEffect(() => {
    const handleCartUpdate = () => {
      const savedCart = localStorage.getItem('nexcom_cart');
      if (savedCart) {
        try {
          const cart = JSON.parse(savedCart);
          const total = cart.reduce((sum, item) => sum + item.quantity, 0);
          setCartCount(total);
        } catch (e) {
          setCartCount(0);
        }
      } else {
        setCartCount(0);
      }
    };

    handleCartUpdate();
    window.addEventListener('storage', handleCartUpdate);
    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('storage', handleCartUpdate);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Scroll handler for sticky header
  useEffect(() => {
    const handleScroll = () => {
      const header = document.getElementById('header');
      if (header) setIsSticky(window.pageYOffset > header.offsetTop);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dropdownLinks = [
    { label: t('nav.login'), url: "https://nexcom.onelynq.app/login", icon: FaUser },
    { label: t('nav.signup'), url: "https://nexcom.onelynq.app/signup", icon: FaUserPlus },
  ];

  const quickLinks = {
    findUs: [
      { name: "Facebook", link: "https://www.facebook.com/NexcomKe", icon: FaFacebook },
      { name: "Twitter", link: "https://twitter.com/NexcomKe?s=20", icon: FaTwitter },
      { name: "Instagram", link: "https://instagram.com/NexcomKe", icon: FaInstagram },
      { name: "Whatsapp", link: "https://api.whatsapp.com/send?phone=254730320320", icon: FaWhatsapp },
    ],
  };

  // Scroll to section helper (only for home page sections)
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const headerOffset = 80;
      const elementPosition = section.offsetTop;
      window.scrollTo({
        top: elementPosition - headerOffset,
        behavior: 'smooth'
      });
    }
  };

  // Open cart
  const openCart = () => {
    window.dispatchEvent(new CustomEvent('openCart'));
    if (window.location.pathname !== '/devices') {
      window.location.href = '/devices';
    }
  };

  return (
    <div className="app">
      {/* ─── HEADER ─── */}
      <header className="header" id="header-area">
        <div className={`main-nav ${isSticky ? 'sticky' : ''}`} id="header">
          <div className="main-nav-inner">
            <Link to="/" className="logo">
              <img src={nexcomLogo} alt="Nexcom Internet Services" className="logo-image" />
            </Link>

            <nav className="desktop-nav">
              <Link to="/#internet" className="nav-link" onClick={() => scrollToSection('internet')}>
                <span>{t('nav.internet')}</span>
              </Link>
              <Link to="/#wifi" className="nav-link" onClick={() => scrollToSection('wifi')}>
                <span>{t('nav.wifi')}</span>
              </Link>
              <Link to="/devices" className="nav-link">
                <span>{t('nav.devices')}</span>
              </Link>
            </nav>

            <div className="nav-right">
              {/* Cart Button */}
              <button
                onClick={openCart}
                style={{
                  position: 'relative',
                  background: 'transparent',
                  border: 'none',
                  color: '#a1a1aa',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginRight: '0.5rem',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#f97316'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#a1a1aa'}
              >
                <FaShoppingCart size={20} />
                {cartCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-4px',
                    background: '#f97316',
                    color: '#ffffff',
                    fontSize: '0.5rem',
                    fontWeight: 700,
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>

              {/* Get Started Dropdown - Now before Language */}
              <div className="dropdown-wrapper" ref={dropdownRef}>
                <button 
                  className={`nav-get-started ${isDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {t('nav.getStarted')} <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    {dropdownLinks.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <a
                          key={index}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <Icon className="dropdown-item-icon" />
                          <span>{item.label}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Language Dropdown - Now after Get Started (far right) */}
              <div className="lang-dropdown-wrapper" ref={langDropdownRef}>
                <button 
                  className={`lang-dropdown-btn ${isLangDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                >
                  <FaGlobe className="lang-icon" />
                  <span className="lang-current-label">
                    {language === 'en' ? 'EN' : 'SW'}
                  </span>
                  <FaChevronDown className={`lang-dropdown-arrow ${isLangDropdownOpen ? 'rotated' : ''}`} />
                </button>
                
                {isLangDropdownOpen && (
                  <div className="lang-dropdown-menu">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        className={`lang-dropdown-item ${language === option.code ? 'active' : ''}`}
                        onClick={() => {
                          toggleLanguage(option.code);
                          setIsLangDropdownOpen(false);
                        }}
                      >
                        <span className="lang-option-label">{option.label}</span>
                        {language === option.code && (
                          <span className="lang-check">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-toggle"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </header>

      {/* ─── MOBILE MENU ─── */}
      {isMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <button onClick={() => setIsMenuOpen(false)} className="mobile-menu-close">
                <FaTimes />
              </button>
            </div>
            <div className="mobile-menu-links">
              <Link to="/#internet" className="mobile-link" onClick={() => { setIsMenuOpen(false); scrollToSection('internet'); }}>
                <FaHouseUser className="mobile-link-icon" />
                <span>{t('nav.internet')}</span>
              </Link>
              <Link to="/#wifi" className="mobile-link" onClick={() => { setIsMenuOpen(false); scrollToSection('wifi'); }}>
                <FaWifi className="mobile-link-icon" />
                <span>{t('nav.wifi')}</span>
              </Link>
              <Link to="/devices" className="mobile-link" onClick={() => setIsMenuOpen(false)}>
                <FaServer className="mobile-link-icon" />
                <span>{t('nav.devices')}</span>
              </Link>
              <Link to="/contact" className="mobile-link" onClick={() => setIsMenuOpen(false)}>
                <FaEnvelope className="mobile-link-icon" />
                <span>{t('nav.contact')}</span>
              </Link>
              
              <div className="mobile-divider"></div>
              
              {/* Mobile Language Toggle */}
              <div className="mobile-language-section">
                <span className="mobile-language-label">Language / Lugha</span>
                <div className="mobile-language-buttons">
                  {languageOptions.map((option) => (
                    <button 
                      key={option.code}
                      className={`mobile-lang-btn ${language === option.code ? 'active' : ''}`}
                      onClick={() => {
                        toggleLanguage(option.code);
                        setIsMenuOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="mobile-divider"></div>
              
              <button 
                className="mobile-link"
                onClick={() => {
                  setIsMenuOpen(false);
                  openCart();
                }}
              >
                <FaShoppingCart className="mobile-link-icon" />
                <span>{t('nav.cart')} {cartCount > 0 && `(${cartCount})`}</span>
              </button>
              <a 
                href="https://nexcom.onelynq.app/login"
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <FaUser className="mobile-link-icon" />
                <span>{t('nav.login')}</span>
              </a>
              <a 
                href="https://nexcom.onelynq.app/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <FaUserPlus className="mobile-link-icon" />
                <span>{t('nav.signup')}</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ─── PAGE CONTENT ─── */}
      {children}

      {/* ─── FOOTER ─── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">
                <Link to="/">
                  <img src={nexcomLogo} alt="Nexcom Internet Services" className="footer-logo-image" />
                </Link>
              </div>
              <p className="footer-brand-text">
                {t('footer.brandText')}
              </p>
              <div className="footer-socials">
                {quickLinks.findUs.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" className="footer-social">
                      <Icon />
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="footer-col">
              <h4>{t('footer.services')}</h4>
              <ul>
                <li>
                  <Link to="/#internet" className="footer-link" onClick={() => scrollToSection('internet')}>
                    {t('nav.internet')}
                  </Link>
                </li>
                <li>
                  <Link to="/#wifi" className="footer-link" onClick={() => scrollToSection('wifi')}>
                    {t('nav.wifi')}
                  </Link>
                </li>
                <li>
                  <Link to="/devices" className="footer-link">
                    {t('nav.devices')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t('footer.support')}</h4>
              <ul>
                <li>
                  <Link to="/support" className="footer-link">
                    {t('footer.faqs')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link">
                    {t('footer.contactUs')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link">
                    {t('footer.quickPay')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="footer-link">
                    {t('footer.requestCallback')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t('footer.about')}</h4>
              <ul>
                <li>
                  <Link to="/about" className="footer-link">
                    {t('footer.aboutUs')}
                  </Link>
                </li>
                <li>
                  <Link to="/coverage" className="footer-link">
                    {t('footer.coverageMap')}
                  </Link>
                </li>
                <li>
                  <Link to="/careers" className="footer-link">
                    {t('footer.careers')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>{t('footer.legal')}</h4>
              <ul>
                <li>
                  <Link to="/legal/privacy" className="footer-link">
                    {t('footer.privacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/terms" className="footer-link">
                    {t('footer.terms')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/cookies" className="footer-link">
                    {t('footer.cookies')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/dataprotection" className="footer-link">
                    {t('footer.dataProtection')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <span>
              &copy; {currentYear} Nexcom. {t('footer.rights')}
            </span>
            <div className="footer-legal">
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}